/* Stem 分軌伴奏 — 播放器（主執行緒）
 * 分軌在 engine-worker.js（Web Worker）；這裡負責：解碼、邊分邊播排程、推子/M/S、原曲切換、移調、時間軸。
 * 音訊路徑：原曲 → origGain ─┐
 *           6 軌 → stemGain[k] → analyser → mixGain ─┤→ preBus →（移調時經 SoundTouch）→ outBus → 喇叭
 * outBus 是日後「錄影」要接的出口（MediaStreamDestination 接這裡），先預留。
 */
import { SoundTouchNode } from './mc-stem-st-node.js';

const SR = 44100, STEP_SEC = 158760 / SR, MAX_SEC = 600;
const PITCH_LATENCY = 0.14; // SoundTouch 實測延遲約 140ms（±2 半音、440Hz 純音）；游標顯示扣掉它，日後錄影對齊也要用
const STEMS = [['drums', '鼓', 'Drums'], ['bass', '貝斯', 'Bass'], ['vocals', '人聲', 'Vocals'], ['guitar', '吉他', 'Guitar'], ['keys', '鍵盤', 'Keys'], ['other', '其他', 'Other']];
const root = document.getElementById('stemApp');
const BASE = location.href;
const CONFIG = JSON.parse(root.dataset.config); // section 以 Liquid 產生：asset_url／file_url 的完整網址
const $ = id => document.getElementById(id);

// ---------- 狀態 ----------
let ctx = null, orig = null, dur = 0, frames = 0;
let segs = STEMS.map(() => []);            // 每軌已分好的片段 [{start(frame), buf}]
let processed = 0;                          // 已定稿到第幾秒
let done = false, avgChunkMs = null, backend = null;
let worker = null, workerReady = null, stConfigUrl = null;
let nodes = null;                           // Web Audio 節點
let sources = [];                           // 目前排程中的 source
let playing = false, startCtx = 0, startPos = 0, pos = 0, waiting = false;
let mode = 'mix', pitch = 0;
const vol = {}, muted = {}, solo = {};
STEMS.forEach(([k]) => { vol[k] = 100; muted[k] = false; solo[k] = false; });

// ---------- 引擎 ----------
function startWorker() {
  if (worker) return workerReady;
  try { worker = new Worker(CONFIG.worker); }
  catch (e) { // 萬一 asset 網域與頁面不同源，改用 blob 啟動（worker 內一律用絕對網址）
    const src = `importScripts(${JSON.stringify(new URL(CONFIG.worker, BASE).href)});`;
    worker = new Worker(URL.createObjectURL(new Blob([src], { type: 'text/javascript' })));
  }
  stConfigUrl = new URL(CONFIG.soundtouch, BASE).href;
  workerReady = new Promise((res, rej) => {
    worker.onmessage = ({ data: d }) => {
      if (d.type === 'status') onStatus(d);
      if (d.type === 'status' && d.phase === 'ready') { backend = d.backend; worker.onmessage = onWorker; res(); }
      if (d.type === 'error') rej(new Error(d.message));
    };
    worker.onerror = e => rej(new Error(e.message || '引擎無法啟動'));
    worker.postMessage({ type: 'init', base: BASE, config: CONFIG, preferGpu: true });
  });
  return workerReady;
}
function resetWorker() { if (worker) worker.terminate(); worker = null; workerReady = null; }

function onStatus(d) {
  if (d.phase === 'load-engine') { setStatus('載入分軌引擎…', ''); $('noGpu').hidden = d.backend !== 'wasm'; }
  if (d.phase === 'load-model') {
    const mb = x => (x / 1e6).toFixed(0);
    setStatus(d.cached ? '從這台電腦載入分軌模型…' : '下載分軌模型（只有第一次需要）', `${mb(d.loaded)} / ${mb(d.total)} MB`);
    setBar(d.loaded / d.total);
  }
}
function onWorker({ data: d }) {
  if (d.type === 'segment') addSegment(d);
  else if (d.type === 'status' && d.phase === 'separating') { avgChunkMs = d.avgChunkMs; updateSepStatus(d.done, d.chunks); }
  else if (d.type === 'done') { done = true; processed = dur; setBar(null); setStatus('分軌完成', '可以自由拖動與調整', true); refreshPlayable(); drawWave(); }
  else if (d.type === 'error') showErr('分軌失敗：' + d.message);
}

// ---------- 載入檔案 ----------
$('file').addEventListener('change', e => { const f = e.target.files[0]; if (f) loadFile(f); e.target.value = ''; });
const drop = $('drop');
['dragenter', 'dragover'].forEach(t => drop.addEventListener(t, e => { e.preventDefault(); drop.classList.add('over'); }));
['dragleave', 'drop'].forEach(t => drop.addEventListener(t, e => { e.preventDefault(); drop.classList.remove('over'); }));
drop.addEventListener('drop', e => { const f = e.dataTransfer.files[0]; if (f) loadFile(f); });
$('another').onclick = () => { stop(); resetWorker(); $('player').hidden = true; $('drop').hidden = false; $('file').click(); };

async function ensureAudio() {
  if (ctx) return;
  ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: SR, latencyHint: 'playback' });
  const n = nodes = { origGain: ctx.createGain(), mixGain: ctx.createGain(), preBus: ctx.createGain(), outBus: ctx.createGain(), stem: {}, anal: {}, st: null };
  n.origGain.connect(n.preBus); n.mixGain.connect(n.preBus); n.outBus.connect(ctx.destination);
  STEMS.forEach(([k]) => { const g = ctx.createGain(), a = ctx.createAnalyser(); a.fftSize = 512; g.connect(a); a.connect(n.mixGain); n.stem[k] = g; n.anal[k] = a; });
  try { await SoundTouchNode.register(ctx, new URL(CONFIG.soundtouch, BASE).href); n.st = new SoundTouchNode({ context: ctx }); n.st.connect(n.outBus); } catch (e) { n.st = null; }
  routePitch(); applyGains();
}

async function loadFile(file) {
  hideErr(); stop();
  $('drop').hidden = true; $('player').hidden = false; $('fname').textContent = file.name;
  segs = STEMS.map(() => []); processed = 0; done = false; avgChunkMs = null; pos = 0; peaks = null;
  $('play').disabled = true; setStatus('讀取音檔…', ''); setBar(0);
  await ensureAudio();
  const enginePromise = startWorker();       // 模型下載與音檔解碼同時進行
  let decoded;
  try { decoded = await ctx.decodeAudioData(await file.arrayBuffer()); }
  catch (e) { return fail('這個檔案讀不出來，請換成 MP3、M4A 或 WAV。'); }
  if (decoded.duration > MAX_SEC) return fail(`這首歌 ${fmt(decoded.duration)}，超過 10 分鐘上限。請先剪短再放進來。`);
  const L = decoded.getChannelData(0), R = decoded.numberOfChannels > 1 ? decoded.getChannelData(1) : L;
  orig = decoded.numberOfChannels > 1 ? decoded : (() => { const b = ctx.createBuffer(2, decoded.length, SR); b.copyToChannel(L, 0); b.copyToChannel(L, 1); return b; })();
  dur = orig.duration; frames = orig.length; drawWave(); tick();
  try { await enginePromise; } catch (e) { return fail('分軌引擎載入失敗：' + e.message + '。請重新整理再試一次。'); }
  setStatus('分軌中 0%', '第一段分好就能開始播放'); setBar(0);
  worker.postMessage({ type: 'separate', left: L.slice(), right: R.slice() });
}
function fail(msg) { showErr(msg); $('player').hidden = true; $('drop').hidden = false; resetWorker(); }

function addSegment(d) {
  const s0 = d.start;
  d.stems.forEach(([l, r], i) => {
    const b = ctx.createBuffer(2, d.length, SR); b.copyToChannel(l, 0); b.copyToChannel(r, 1);
    const seg = { start: s0, buf: b }; segs[i].push(seg);
    if (playing) schedule(i, seg); // 原曲模式也照排，切回混音才不會斷
  });
  processed = Math.min(dur, (s0 + d.length) / SR);
  refreshPlayable(); drawWave();
}
function updateSepStatus(k, n) {
  const pct = Math.round(k / n * 100); setBar(k / n);
  if (done) return;
  const left = (n - k) * avgChunkMs / 1000;
  const sub = canStart() ? `可以開始播放・全部分完還要約 ${Math.ceil(left)} 秒` : `約 ${Math.ceil(waitEstimate())} 秒後可以開始播放`;
  if (!waiting) setStatus(`分軌中 ${pct}%`, sub);
}
// 開播規則：剩下要分的時間 ≤ 從目前位置播到結尾的時間（留 10% 餘裕），且至少分好 2 段
function speed() { return avgChunkMs ? STEP_SEC / (avgChunkMs / 1000) : 0; }
function canStart() {
  if (done) return true;
  if (processed < STEP_SEC * 2 - 0.01 || !speed()) return false;
  return (dur - processed) / speed() <= (dur - pos) * 0.9;
}
function waitEstimate() { const v = speed(); if (!v) return 10; const need = Math.max(STEP_SEC * 2, dur - (dur - pos) * 0.9 * v); return Math.max(0, (need - processed) / v); }
function refreshPlayable() {
  $('play').disabled = !(playing || canStart() || mode === 'orig');
  if (waiting && (done || processed - pos > STEP_SEC)) { waiting = false; play(); }
}

// ---------- 播放排程 ----------
function schedule(i, seg) {
  const segStart = seg.start / SR, segEnd = segStart + seg.buf.duration;
  if (segEnd <= startPos) return;
  // 片段在時間軸上的起點換算成 AudioContext 時間；已經晚了就從對應位置切進去
  let t = startCtx + Math.max(0, segStart - startPos), o = Math.max(0, startPos - segStart);
  const now = ctx.currentTime;
  if (t < now) { o += now - t; t = now; }
  if (o >= seg.buf.duration) return;
  const src = ctx.createBufferSource(); src.buffer = seg.buf; src.connect(nodes.stem[STEMS[i][0]]);
  src.start(t, o); sources.push(src);
}
function play() {
  if (!orig) return;
  if (ctx.state === 'suspended') ctx.resume();
  stopSources();
  if (pos >= dur - 0.05) pos = 0;
  pos = Math.round(pos * SR) / SR; // 對齊取樣點，避免次樣本內插
  startCtx = ctx.currentTime + 0.08; startPos = pos;
  const o = ctx.createBufferSource(); o.buffer = orig; o.connect(nodes.origGain); o.start(startCtx, pos); sources.push(o);
  segs.forEach((list, i) => list.forEach(seg => schedule(i, seg)));
  playing = true; waiting = false; setIcon();
}
function stopSources() { sources.forEach(s => { try { s.stop(); } catch (e) {} }); sources = []; }
function stop() { if (!ctx) return; if (playing) pos = curPos(); stopSources(); playing = false; setIcon(); }
function curPos() { return playing ? Math.max(0, Math.min(dur, startPos + ctx.currentTime - startCtx)) : pos; }
$('play').onclick = () => { if (playing) stop(); else play(); };
function setIcon() { $('icoPlay').innerHTML = playing ? '<path d="M7 5h4v14H7zM13 5h4v14h-4z"/>' : '<path d="M8 5v14l11-7z"/>'; $('play').setAttribute('aria-label', playing ? '暫停' : '播放'); }

// ---------- 推子／M／S／模式 ----------
$('mixer').innerHTML = STEMS.map(([k, zh, en]) => `
  <div class="strip" data-k="${k}">
    <h3>${zh}<small>${en}</small></h3>
    <div class="fwrap"><div class="ftrack"><input class="fader" type="range" min="0" max="150" value="100" id="f-${k}" aria-label="${zh}音量"><i class="tick100" aria-hidden="true"></i></div><div class="meter"><span id="m-${k}"></span></div></div>
    <div class="val" id="v-${k}">100%</div>
    <div class="ms"><button class="mute" type="button" id="mu-${k}" aria-pressed="false" aria-label="${zh}靜音">M</button><button class="mute solo" type="button" id="so-${k}" aria-pressed="false" aria-label="${zh}獨奏">S</button></div>
  </div>`).join('');
STEMS.forEach(([k]) => {
  const f = $('f-' + k);
  f.addEventListener('input', () => { vol[k] = +f.value; applyGains(); });
  f.addEventListener('dblclick', () => { vol[k] = 100; applyGains(); });
  $('mu-' + k).onclick = () => { muted[k] = !muted[k]; applyGains(); };
  $('so-' + k).onclick = () => { solo[k] = !solo[k]; applyGains(); };
});
function applyGains() {
  const t = ctx ? ctx.currentTime : 0;
  const anySolo = STEMS.some(([k]) => solo[k]);   // Solo 規則：有 Solo 只播 Solo 軌；Solo＋靜音 → 靜音優先
  STEMS.forEach(([k]) => {
    const off = muted[k] || (anySolo && !solo[k]);
    if (nodes) nodes.stem[k].gain.setTargetAtTime(off ? 0 : vol[k] / 100, t, 0.015);
    $('v-' + k).textContent = muted[k] ? '靜音' : (anySolo && !solo[k]) ? '（Solo 中）' : vol[k] + '%';
    $('mu-' + k).setAttribute('aria-pressed', muted[k]); $('so-' + k).setAttribute('aria-pressed', solo[k]);
    $('f-' + k).value = vol[k];
    const st = document.querySelector(`.strip[data-k="${k}"]`);
    st.classList.toggle('zero', off || vol[k] === 0); st.classList.toggle('boost', vol[k] > 100);
  });
  if (nodes) { nodes.origGain.gain.setTargetAtTime(mode === 'orig' ? 1 : 0, t, 0.015); nodes.mixGain.gain.setTargetAtTime(mode === 'mix' ? 1 : 0, t, 0.015); }
  $('mixer').classList.toggle('off', mode === 'orig');
  $('mixHint').textContent = mode === 'orig' ? '正在聽原曲：切回「分軌混音」才會套用推子' : '拖推子調音量（最大 150%）・M＝靜音・S＝獨奏（可多軌）・雙擊推子回到 100%';
  document.querySelectorAll('#modeSeg button').forEach(b => b.setAttribute('aria-pressed', b.dataset.m === mode));
  $('noGtr').setAttribute('aria-pressed', mode === 'mix' && (vol.guitar === 0 || muted.guitar));
  if (orig) refreshPlayable();
}
document.querySelectorAll('#modeSeg button').forEach(b => b.onclick = () => { mode = b.dataset.m; applyGains(); });
$('noGtr').onclick = () => { const on = $('noGtr').getAttribute('aria-pressed') === 'true'; mode = 'mix'; vol.guitar = on ? 100 : 0; muted.guitar = false; applyGains(); };

// ---------- 移調（SoundTouch；0 半音時繞過，避免無謂處理）----------
function routePitch() {
  if (!nodes) return;
  const n = nodes;
  try { n.preBus.disconnect(); } catch (e) {}
  if (pitch !== 0 && n.st) { n.st.pitchSemitones.value = pitch; n.preBus.connect(n.st); }
  else n.preBus.connect(n.outBus);
}
document.querySelectorAll('#pitchSeg button').forEach(b => b.onclick = () => {
  pitch = +b.dataset.p; document.querySelectorAll('#pitchSeg button').forEach(x => x.setAttribute('aria-pressed', x === b)); routePitch();
});

// ---------- 時間軸（拖曳／點擊）----------
let drag = null;
const limit = () => mode === 'orig' || done ? dur : Math.max(0, processed - 0.5);
const posAt = e => { const r = $('tl').getBoundingClientRect(); return Math.max(0, Math.min((e.clientX - r.left) / r.width * dur, limit())); };
$('tl').addEventListener('pointerdown', e => { if (!orig) return; $('tl').setPointerCapture(e.pointerId); drag = { was: playing }; if (playing) stop(); pos = posAt(e); $('tl').classList.add('dragging'); });
$('tl').addEventListener('pointermove', e => { if (drag) pos = posAt(e); });
const endDrag = e => { if (!drag) return; pos = posAt(e); $('tl').classList.remove('dragging'); const r = drag.was; drag = null; if (r) play(); };
$('tl').addEventListener('pointerup', endDrag); $('tl').addEventListener('pointercancel', endDrag);

// ---------- 畫面更新 ----------
const fmt = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
function tick() {
  if (orig && ctx) {
    const p = curPos();
    // 混音模式播到還沒分好的地方 → 暫停等待，分好自動接著播
    if (playing && mode !== 'orig' && !done && p >= processed - 0.1) { stop(); pos = p; waiting = true; setStatus('等待分軌…', '播放追上分軌進度，分好會自動接著播'); }
    if (playing && p >= dur - 0.02) { stop(); pos = 0; }
    const shown = drag ? pos : Math.max(0, p - (playing && pitch !== 0 && nodes.st ? PITCH_LATENCY : 0));
    $('ph').style.left = (shown / dur * 100) + '%';
    $('clock').innerHTML = `${fmt(shown)} <small>/ ${fmt(dur)}</small>`;
    $('tlTime').textContent = `${fmt(shown)} / ${fmt(dur)}`;
    STEMS.forEach(([k]) => {
      const a = nodes.anal[k], d = new Float32Array(a.fftSize); a.getFloatTimeDomainData(d);
      let m = 0; for (const v of d) m = Math.max(m, Math.abs(v));
      $('m-' + k).style.height = Math.min(100, Math.max(0, (20 * Math.log10(m + 1e-6) + 48) / 48 * 100)) + '%';
    });
  }
  requestAnimationFrame(tick);
}
let peaks = null;
function drawWave() {
  const c = $('wave'), w = c.clientWidth, h = c.clientHeight, dpr = devicePixelRatio || 1;
  if (!w || !orig) return;
  if (c.width !== w * dpr) { c.width = w * dpr; c.height = h * dpr; }
  const g = c.getContext('2d'); g.setTransform(dpr, 0, 0, dpr, 0, 0); g.clearRect(0, 0, w, h);
  if (!peaks) { const d = orig.getChannelData(0), n = 300, step = Math.floor(d.length / n); peaks = []; for (let i = 0; i < n; i++) { let m = 0; for (let j = i * step; j < (i + 1) * step; j += 32) m = Math.max(m, Math.abs(d[j])); peaks.push(m); } }
  const mx = Math.max(...peaks, 1e-6), bw = w / peaks.length, edge = (done ? dur : processed) / dur * w;
  peaks.forEach((p, i) => { const x = i * bw, bh = Math.max(2, p / mx * (h - 12)); g.fillStyle = x < edge ? '#536B4F' : '#3a3633'; g.fillRect(x + 0.5, (h - bh) / 2, Math.max(1, bw - 1.5), bh); });
  if (!done && processed < dur) { g.fillStyle = 'rgba(178,138,47,.9)'; g.fillRect(edge - 1, 0, 2, h); }
}
addEventListener('resize', drawWave);
function setStatus(a, b, ok) { $('stTxt').textContent = a; $('stSub').textContent = b; $('dot').classList.toggle('done', !!ok); }
function setBar(f) { $('pbar').hidden = f == null; if (f != null) $('pbarFill').style.width = Math.round(f * 100) + '%'; }
function showErr(m) { $('err').textContent = m; $('err').hidden = false; }
function hideErr() { $('err').hidden = true; }
applyGains();

